import { MutableRefObject, useEffect, useRef } from 'react';

import { GridApi, gridColumnPositionsSelector } from '@mui/x-data-grid-premium';

import dayjs, { Dayjs } from 'dayjs';

import { TimelineRange } from '../timeline/timeline.types';
import { getPeriodFieldName } from '../timeline/timeline.utils';

/**
 * Where the focus date sits in the scrolled content, and where in the viewport that is the middle.
 *
 * Neither is the number it looks like. `offsetForDate` measures from the first period column, but
 * the content scrolls as one strip that begins further left — the checkbox and the pinned name
 * column are part of it too. And pinned columns sit over the viewport rather than scrolling with
 * it, so the middle of what a user can actually read the rail in is not the middle of the viewport.
 *
 * Left out, the two errors compound in the same direction: the rail settles a pinned column's
 * width too far right, showing more of the past than of what is coming.
 */
const centringTargets = (apiRef: MutableRefObject<GridApi>, viewportFallback: number) => {
  const positions = gridColumnPositionsSelector(apiRef);
  const firstPeriodIndex = apiRef.current?.getColumnIndex?.(getPeriodFieldName(0), true) ?? -1;
  const dimensions = apiRef.current?.getRootDimensions?.();
  const viewportWidth = dimensions?.viewportInnerSize?.width || viewportFallback;

  return {
    periodsLeft: positions?.[firstPeriodIndex] ?? 0,
    viewportCentre:
      ((dimensions?.leftPinnedWidth ?? 0) + viewportWidth - (dimensions?.rightPinnedWidth ?? 0)) /
      2,
  };
};

/**
 * Brings the part of the calendar that matters into view when a timeline rail opens.
 *
 * A rail spanning three years opens at its far left — January of last year — which is nowhere near
 * the work anyone is doing. Today is centred instead: centred in the stretch of viewport the rail
 * is actually drawn in, which is not the same as the viewport (see [centringTargets]).
 *
 * `focusDate` overrides what gets centred. A bookings rail passes its earliest booking, because a
 * booking is not bound by its order's dates: one sitting months out would otherwise be scrolled off
 * the page that exists to show it, and column virtualisation means it is not even rendered.
 *
 * Runs once per rail: on later renders the user's own scrolling wins, and
 * [usePreserveHorizontalScroll] restores it across the remounts that server-mode rows cause. The
 * two would otherwise fight over the same scroller.
 */
const useScrollTimelineToToday = (
  apiRef: MutableRefObject<GridApi>,
  range: TimelineRange | null,
  dateToOffset: ((date: Dayjs) => number) | null,
  scaleKey: string,
  enabled: boolean,
  focusDate?: Dayjs,
) => {
  const centredFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !range || !dateToOffset) {
      centredFor.current = null;
      return;
    }

    const focus = focusDate ?? dayjs();
    if (focus.isBefore(range.start) || focus.isAfter(range.end)) return;

    const focusOffset = dateToOffset(focus);

    // One centring per drawn range. Re-centring on every render would yank the rail back each time
    // the user scrolled away from today. The focusOffset term matters beyond the obvious dedupe: in
    // bookings mode the interior segment map can change while the outer bounds stay identical,
    // silently moving the focus's pixel position — keying on the resolved offset re-centres exactly
    // when the drawn position of the focus actually moved.
    const rangeKey = `${range.start.valueOf()}-${range.end.valueOf()}-${scaleKey}-${focus.valueOf()}-${Math.round(focusOffset)}`;
    if (centredFor.current === rangeKey) return;

    const scroller = apiRef.current?.rootElementRef?.current?.querySelector<HTMLElement>(
      '.MuiDataGrid-virtualScroller',
    );
    if (!scroller) return;

    // Two frames: MUI lays the period columns out first, so before that the scroller has no width
    // to scroll within and the assignment is silently dropped.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scroller.scrollWidth <= scroller.clientWidth) return;

        const { periodsLeft, viewportCentre } = centringTargets(apiRef, scroller.clientWidth);

        scroller.scrollLeft = Math.max(0, periodsLeft + focusOffset - viewportCentre);
        centredFor.current = rangeKey;
      });
    });
  }, [apiRef, range, dateToOffset, scaleKey, enabled, focusDate]);
};

export default useScrollTimelineToToday;
